import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { getRoomTypeCapacity } from 'lib/tours/tourUtils'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'

interface BaseAdditionalOptions {
  skip?: boolean
  loyaltyProductType?: App.LoyaltyDefaultPointsEarnCalculationRequest['loyaltyProductType']
}

interface BasePricingOptions {
  price?: number
  memberPrice?: number
}

interface GenerateLoyaltyPointsCalculatorHotelAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  currentRegionCode: string
  packageView?: App.OfferPackageOptionView
  pkg?: App.HotelPackage
}

export function generateLoyaltyPointsCalculatorHotelOptions(
  offer: App.Offer | App.OfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorHotelAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    packageView,
    pkg,
    skip,
    price,
    memberPrice,
    currentRegionCode,
  } = options
  const loyaltyProductType = getLoyaltyProductType(offer)

  if (
    !offer ||
    (!packageView && !pkg) ||
    (!loyaltyProductType || (loyaltyProductType !== 'hotel_flash' && loyaltyProductType !== 'hotel_lpc')) ||
    skip
  ) return

  const packageViewPrice = packageView?.pricing?.price ?? 0
  const packageViewMemberPrice = packageView?.memberPricing?.price ?? 0

  const packageViewPropertyFees = packageView?.pricing?.fees?.find((fee) => fee.type === 'property')?.amount ?? 0

  const packageViewPriceExcludingPropertyFees = packageViewPrice - packageViewPropertyFees
  const packageViewMemberPriceExcludingPropertyFees = packageViewMemberPrice > 0 ? packageViewMemberPrice - packageViewPropertyFees : 0
  const propertyCountryCode = offer.property?.geoData?.countryCode

  let earnKey: string | undefined

  if (loyaltyProductType === 'hotel_lpc') {
    earnKey = getLoyaltyEarnKey({ loyaltyProductType, options: { propertyCountryCode, currentRegionCode } })
  }

  return {
    price: price ?? packageViewPriceExcludingPropertyFees,
    memberPrice: memberPrice ?? packageViewMemberPriceExcludingPropertyFees,
    loyaltyProductType,
    mx: getHotelEncryptedMargin(offer.type, packageView?.package ?? pkg),
    offerId: offer.id,
    packageId: packageView?.package.id ?? pkg?.id ?? '',
    earnKey,
  }
}

interface GenerateLoyaltyPointsCalculatorTourAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

interface GenerateLoyaltyPointsCalculatorBedbankAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  propertyFees: number
  propertyId: string
  offerId: string
  propertyCountryCode: string
  currentRegionCode: string
}

interface GenerateLoyaltyPointsCalculatorPropertyAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  propertyCountryCode?: string
  currentRegionCode: string
}

export function getLoyaltyBedbankPackageId(propertyId: string | undefined, supplierCode: string | undefined) {
  if (!propertyId || !supplierCode) return ''
  return `${propertyId}:${supplierCode}`
}

export function generateLoyaltyPointsCalculatorBedbankOptions(
  rate: App.BedbankRate | undefined,
  options: GenerateLoyaltyPointsCalculatorBedbankAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price = 0,
    memberPrice = 0,
    propertyFees,
    propertyId,
    offerId,
    propertyCountryCode,
    currentRegionCode,
  } = options

  if (skip) return

  return {
    price: price - propertyFees,
    memberPrice: memberPrice > 0 ? memberPrice - propertyFees : 0,
    loyaltyProductType: 'hotel_lpp_eps',
    mx: rate?.mx,
    packageId: getLoyaltyBedbankPackageId(propertyId, rate?.supplierCode),
    offerId,
    earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode, currentRegionCode } }),
  }
}

export function generateLoyaltyPointsCalculatorPropertyOptions(
  rate: App.PropertyRate,
  options: GenerateLoyaltyPointsCalculatorPropertyAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest {
  const baseRequest = {
    price: rate.total.societeHotelPrice,
    memberPrice: rate.luxPlusTotal?.societeHotelPrice ?? 0,
    // TODO(Xiao): `mx` is not included on luxPlus???
    mx: rate.total.mx,
  }
  const { propertyCountryCode, currentRegionCode } = options

  if (rate.type === 'lpc') {
    return {
      ...baseRequest,
      loyaltyProductType: 'hotel_lpc',
      offerId: rate.offerId,
      packageId: rate.packageId,
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpc', options: { propertyCountryCode, currentRegionCode } }),
    }
  } else if (rate.type === 'bedbank') {
    return {
      ...baseRequest,
      loyaltyProductType: 'hotel_lpp_eps',
      packageId: getLoyaltyBedbankPackageId(rate.bedbankPropertyId, rate.bedbankSupplierCode),
      offerId: rate.bedbankPropertyId,
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode, currentRegionCode } }),
    }
  } else {
    assertUnreachable(rate)
  }
}

interface GenerateLoyaltyPointsCalculatorVillasAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  pkg: App.HotelPackage | undefined
  propertyFees: number
}

export function generateLoyaltyPointsCalculatorVillasOptions(
  offer: App.VillaOffer | App.VillaOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorVillasAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    pkg,
    skip,
    price = 0,
    memberPrice = 0,
    propertyFees,
  } = options

  if (skip || !offer || !pkg) return

  const priceExcludingPropertyFees = price - propertyFees
  const memberPriceExcludingPropertyFees = memberPrice > 0 ? memberPrice - propertyFees : 0

  return {
    price: priceExcludingPropertyFees,
    memberPrice: memberPriceExcludingPropertyFees,
    loyaltyProductType: 'rental',
    mx: pkg.roomRate.mx,
    offerId: offer.id,
    packageId: pkg.id,
  }
}

interface FlightPricingOptions {
  flightPrice?: number
}

interface GenerateLoyaltyPointsCalculatorFlightAdditionalOptions extends BaseAdditionalOptions, FlightPricingOptions {
  packageView?: App.OfferPackageOptionView
}

export function generateLoyaltyPointsCalculatorBundledFlightOptions(
  offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorFlightAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    packageView,
    flightPrice,
  } = options ?? {}

  if (
    (isBedbankOffer(offer) && !offer.flights.flightsEnabled) ||
    (!isBedbankOffer(offer) && !offer?.offerFlightsEnabled) ||
    skip
  ) {
    return
  }

  return {
    price: flightPrice ?? packageView?.flightPrice ?? 0,
    loyaltyProductType: 'dom_economy',
  }
}

export function generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(
  rate: App.PropertyRate,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (!(rate.includesFlights && typeof rate.cheapestFlightPrice === 'number')) return
  return {
    price: rate.cheapestFlightPrice,
    loyaltyProductType: 'dom_economy',
  }
}

interface GenerateLoyaltyPointsCalculatorInsuranceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorInsuranceOptions(
  options?: GenerateLoyaltyPointsCalculatorInsuranceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
    loyaltyProductType,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? 0,
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: loyaltyProductType ?? 'insurance',
  }
}

interface GenerateLoyaltyPointsCalculatorExperienceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorExperienceOptions(
  offer: App.ExperienceOffer | undefined,
  options: GenerateLoyaltyPointsCalculatorExperienceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    memberPrice,
  } = options ?? {}

  if (skip) return

  return {
    price: offer?.price ?? 0,
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: 'experience',
  }
}

interface GenerateLoyaltyPointsCalculatorTourAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  roomType?: App.Tours.V2OfferRoomType
  packageId?: string
  offerId?: string
}

export function generateLoyaltyPointsCalculatorTourOptions(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorTourAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
    roomType,
    packageId,
    offerId,
  } = options
  if (
    !offer ||
    skip
  ) return

  const loyaltyProductType = getLoyaltyProductType(offer)

  if (!loyaltyProductType) return

  return {
    price: (price ?? 0) * getRoomTypeCapacity(roomType),
    memberPrice: (memberPrice ?? 0) * getRoomTypeCapacity(roomType),
    loyaltyProductType,
    packageId: packageId ?? '',
    offerId: offerId ?? '',
  }
}

interface GenerateLoyaltyPointsCalculatorTourExperienceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  count: number
}

export function generateLoyaltyPointsCalculatorTourExperienceOptions(
  tourExperience: App.Tours.TourExperience,
  options?: GenerateLoyaltyPointsCalculatorTourExperienceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    count,
  } = options ?? {}

  if (skip) return

  return {
    price: tourExperience.price * (count ?? 1),
    loyaltyProductType: 'tour_optional_extra',
  }
}

interface GenerateLoyaltyPointsCalculatorCarHireAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  selectedInsurance?: number
  earnKey?: string
}

export function generateLoyaltyPointsCalculatorCarHireOptions(
  offer: App.CarHireOffer | App.CarHireOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorCarHireAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const { skip, memberPrice, earnKey } = options ?? {}

  if (skip) return

  let price = 0

  // At the moment only apply loyalty points to the pay now amount
  if (offer && 'rateOptions' in offer && offer.rateOptions.length) {
    price = offer.rateOptions.reduce((acc, rateOption) => {
      return acc + (rateOption.payNowAmount || 0)
    }, 0)
  } else if (offer && 'totalCharge' in offer) {
    price = offer.totalCharge.payNowAmount
  }

  return {
    price: (price ?? 0) + (options.selectedInsurance ?? 0),
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: 'car_hire',
    earnKey,
  }
}

interface GenerateLoyaltyPointsCalculatorCruiseAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  bookingPrice?: number
}

export function generateLoyaltyPointsCalculatorCruiseOptions(
  offer: App.CruiseOffer | undefined,
  options?: GenerateLoyaltyPointsCalculatorCruiseAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    bookingPrice,
  } = options ?? {}

  const loyaltyProductType = getLoyaltyProductType(offer)

  if (skip || !loyaltyProductType || !offer) return

  const price = bookingPrice || offer.mainDepartureDetails.lowestOverallPriceDetails.total || 0

  return {
    price,
    loyaltyProductType,
    packageId: offer.mainDepartureDetails.id,
    offerId: offer.id,
  }
}

interface GenerateLoyaltyPointsCalculatorTransfersAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorTransfersOptions(
  options?: GenerateLoyaltyPointsCalculatorTransfersAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? 0,
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: 'experience',
  }
}

interface GenerateLoyaltyPointsCalculatorMerchandiseAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorMerchandiseOptions(
  variant: App.MerchandiseOfferVariant | undefined,
  options: GenerateLoyaltyPointsCalculatorMerchandiseAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
  } = options ?? {}

  if (!variant || skip || !price) return

  return {
    price,
    loyaltyProductType: variant.loyalty.productType,
  }
}
